/**
 * Mobile Manual Navigation Component
 *
 * Navigation drawer for mobile help page with manual selection and TOC.
 */

import type { TableOfContentItem, ManualMetadata } from '../../types/manual';
import { scrollToHeading } from '../../utils/markdown';
import styles from './MobileManualNav.module.css';

interface MobileManualNavProps {
  /** Available manuals */
  availableManuals: ManualMetadata[];

  /** Currently selected manual */
  selectedManual: ManualMetadata;

  /** Table of contents */
  tableOfContents: TableOfContentItem[];

  /** Callback when manual is changed */
  onManualChange: (manualId: string) => void;

  /** Callback when navigation should close */
  onClose: () => void;

  /** Whether the drawer is open */
  isOpen: boolean;
}

export const MobileManualNav = ({
  availableManuals,
  selectedManual,
  tableOfContents,
  onManualChange,
  onClose,
  isOpen,
}: MobileManualNavProps) => {
  const handleHeadingClick = (itemId: string) => {
    scrollToHeading(itemId);
    onClose();
  };

  const handleManualChange = (manualId: string) => {
    onManualChange(manualId);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`${styles.overlay} ${isOpen ? styles.overlayOpen : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`${styles.drawer} ${isOpen ? styles.drawerOpen : ''}`}
        aria-label="Navigation menu"
      >
        <div className={styles.header}>
          <h2 className={styles.title}>操作手冊</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close navigation"
          >
            ✕
          </button>
        </div>

        {/* Manual Selection */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>選擇手冊</h3>
          <div className={styles.manualList}>
            {availableManuals.map((manual) => (
              <button
                key={manual.id}
                className={`${styles.manualItem} ${
                  selectedManual.id === manual.id ? styles.manualItemActive : ''
                }`}
                onClick={() => handleManualChange(manual.id)}
              >
                <span className={styles.manualIcon}>{manual.icon}</span>
                <div className={styles.manualInfo}>
                  <span className={styles.manualTitle}>{manual.title}</span>
                  {manual.version && (
                    <span className={styles.manualVersion}>{manual.version}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Table of Contents */}
        {tableOfContents.length > 0 && (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>目錄</h3>
            <nav className={styles.toc} aria-label="Table of Contents">
              <ul className={styles.tocList}>
                {tableOfContents.map((item) => (
                  <li
                    key={item.id}
                    className={`${styles.tocItem} ${styles[`tocLevel${item.level}`]}`}
                  >
                    <a
                      href={`#${item.id}`}
                      className={styles.tocLink}
                      onClick={(e) => {
                        e.preventDefault();
                        handleHeadingClick(item.id);
                      }}
                    >
                      {item.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
      </aside>
    </>
  );
};
